import React, { useState } from 'react';
import CreatableSelect from 'react-select/creatable';

import CustomDropdown from '@/components/CustomDropdown';
import CommonImage from '@/components/CommonImage';
import InputField from '@/components/InputField';
import {
	checkAnyUserType,
	getLoadingState,
	getUserPreference,
	isAdmin,
	isFashionDesigner
} from '@/utils/Util';
import { setSystemUserPreferences } from '@/services/commonService';
import SelectColor from '@/features/assets/components/CommonComponents/SelectColor';
import {
	useCreateNewEmbroideryMutation,
	useCreateNewPrintMutation,
	useCreateNewWashMutation,
	useGetEmbroideryListQuery,
	useGetPrintListQuery,
	useGetWashListQuery,
	useUpdateTechpackChecklistMutation
} from '@/features/design/api/designDetailsApi';
import {
	CHECK_LIST_ACTIONS,
	formatFittingInfoEnum,
	TECHPACK_STATUS_TAG_COLORS,
	transformDropdownOptions
} from '@/features/design/utils/designUtils';
import { toastError } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import CustomTooltip from '@/components/CustomTooltip';
import { useGetEnumsQuery, useGetTagsQuery } from '@/api/commonApis';

const TechpackChecklist = ({
	techpackChecklistState,
	isDesignEdit,
	setTechpackChecklistState,
	productId,
	onGetBodyFabrics,
	onUpdateBodyFabric,
	onParseInputName,
	bodyFabricList = [],
	selectedBodyFabric,
	designDetails,
	reloadTechpack = () => {}
}) => {
	const {
		stylingAndOther,
		fittingType,
		stitching,
		print = [],
		wash = [],
		embroidery = [],
		fabricDyeColor,
		trimsDyeColor,
		action,
		bodyWidth,
		bodyHeight,
		sleeves
	} = techpackChecklistState;
	const userPreference = getUserPreference();
	const [isOpenAccordion, setIsOpenAccordion] = useState(userPreference?.IS_CHECKLIST_ACCORDION_OPEN);

	const { data: printList } = useGetPrintListQuery();
	const { data: washList } = useGetWashListQuery();
	const { data: embroideryList } = useGetEmbroideryListQuery();
	const { data: fittingTypes } = useGetTagsQuery({
		params: {
			page: 0,
			size: 1000,
			type: 'PRODUCT_FITTING_TYPE',
			entityActivityType: 'PRODUCT_TAG_UPDATE'
		}
	});

	const [createNewWash, { isLoading: useGetWashListQueryCreating }] = useCreateNewWashMutation();
	const [createNewPrint, { isLoading: printCreating }] = useCreateNewPrintMutation();
	const [createNewEmbroidery, { isLoading: embroideryCreating }] = useCreateNewEmbroideryMutation();
	const { data: fittingInfoBodyLength } = useGetEnumsQuery({
		path: 'info.nitex.app.product.FittingInfoBodyLength'
	});
	const { data: fittingInfoBodyWidth } = useGetEnumsQuery({
		path: 'info.nitex.app.product.FittingInfoBodyWidth'
	});
	const { data: fittingInfoSleeves } = useGetEnumsQuery({
		path: 'info.nitex.app.product.FittingInfoSleeves'
	});

	const [updateTechpackChecklist, { isLoading: removingAction }] = useUpdateTechpackChecklistMutation();

	const handleChange = (key, value) => {
		setTechpackChecklistState((prev) => ({
			...prev,
			[key]: value
		}));
	};

	const handleUserPreferenceUpdate = async (isOpen) => {
		const userPreference = getUserPreference();

		const body = {
			...userPreference,
			IS_CHECKLIST_ACCORDION_OPEN: isOpen
		};
		await setSystemUserPreferences(body);
	};

	const toggleAccordion = () => {
		handleUserPreferenceUpdate(!isOpenAccordion);
		setIsOpenAccordion((prev) => !prev);
	};

	const handleCreateNewPrint = (value) => {
		createNewPrint({ params: { name: value } })
			.unwrap()
			.then((res) => {
				handleChange('print', [
					{
						name: res?.name,
						value: res?.name,
						id: res?.id,
						label: res?.name
					}
				]);
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const handleCreateWash = (value) => {
		createNewWash({ params: { name: value } })
			.unwrap()
			.then((res) => {
				handleChange('wash', [
					{
						name: res?.name,
						value: res?.name,
						id: res?.id,
						label: res?.name
					}
				]);
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const handleCreateEmbroidery = (value) => {
		createNewEmbroidery({ params: { name: value } })
			.unwrap()
			.then((res) => {
				handleChange('embroidery', [
					{
						name: res?.name,
						value: res?.name,
						id: res?.id,
						label: res?.name
					}
				]);
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const loading = getLoadingState([useGetWashListQueryCreating, printCreating, embroideryCreating]);

	const getBlockDetails = () => {
		// 	Check the “chart info type”:

		// If there is a chart info type and it is not "BLOCK"
		// → return the name of that chart info type.

		// If the chart info type is missing or equals "BLOCK", then:

		// If there is no measurement chart in blockDetailsResponse
		// → return an empty string.

		// If there is a measurement chart AND the chart was copied from something marked as “block” (isBlock = true)
		// → collect name, brandName, and referenceNumber from that copy source
		// and return them in a formatted single string.

		// If there is a measurement chart BUT there is no “copy from” data
		// → return "Measurement chart added".

		// Otherwise
		// → return an empty string.

		const chartInfoType = designDetails?.measurementChartInfoType?.constant || '';

		if (chartInfoType && chartInfoType !== 'BLOCK') {
			return designDetails?.measurementChartInfoType?.name;
		} else {
			if (!designDetails?.blockDetailsResponse?.hasMeasurementChart) {
				return '';
			}
			if (designDetails?.blockDetailsResponse?.hasMeasurementChart) {
				if (designDetails?.blockDetailsResponse?.measurementCopyFromResponse?.isBlock) {
					const name = designDetails?.blockDetailsResponse?.measurementCopyFromResponse?.name;
					const brandName =
						designDetails?.blockDetailsResponse?.measurementCopyFromResponse?.brandName;
					const referenceNumber =
						designDetails?.blockDetailsResponse?.measurementCopyFromResponse?.referenceNumber;

					return `${name ? `${name};` : ''} ${brandName ? `${brandName};` : ''} ${referenceNumber ? `${referenceNumber}` : ''}`;
				} else {
					return `Measurement chart added`;
				}
			} else {
				return '';
			}
		}
	};

	const handleNASelection = (values = []) => {
		const newArray = [...values];
		const lastIndex = newArray.pop();
		if (lastIndex?.label === 'N/A') {
			return values?.filter((item) => item?.label === 'N/A');
		} else {
			return values?.filter((item) => item?.label !== 'N/A');
		}
	};

	const handleRemoveStyleBlock = () => {
		updateTechpackChecklist({
			body: {
				measurementChartInfoType: null
			},
			productId: productId
		})
			.unwrap()
			.then(() => {
				reloadTechpack();
			})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const CheckboxItem = ({ id, label, checked, disabled = true, isShowNA = false, isShowComma = false }) => (
		<div className={`custom-chekbox ${isShowNA ? 'incorrect-checkbox' : ''}`}>
			<div className="form-group mb-0">
				<input
					type="checkbox"
					id={id}
					name={id}
					className="custom-checkbox-input"
					checked={!!checked}
					disabled={!checked && disabled}
					readOnly
				/>
				<label className="regular-14 weight-500 d-flex gray_dark_02 align-items-center" htmlFor={id}>
					<span className="white-space-nowrap">
						{label}
						{isShowComma ? ',' : ''}
					</span>
				</label>
			</div>
		</div>
	);

	const noBorderStyles = {
		control: (base) => ({
			...base,
			borderColor: '#c7ced4',
			backgroundColor: 'transparent',
			boxShadow: 'none',
			'&:hover': {
				borderColor: '#c7ced4'
			}
		})
	};
	const renderCrossIcon = (title) => {
		return (
			<div className="d-flex align-items-center">
				<span className="times-check-box d-flex align-items-center justify-content-center">
					<CommonImage src="/icons/chip-close.svg" width={16} height={16} />
				</span>
				{title && <p className="gray_dark_02 regular-14 weight-500">{title}</p>}
			</div>
		);
	};

	return (
		<LoaderComponent loading={loading || removingAction}>
			<div className="techpack-checklist mb-3">
				<div className="common-accordion">
					<div className="accordion" id="accordionExample">
						<div className="card mb-0">
							<div className="card-header" id="headingChecklist">
								<h2 className="mb-0">
									<button
										className={`btn btn-link btn-block d-flex justify-content-between text-left gray_dark_01 regular-14 weight-600 p-0 ${isOpenAccordion ? '' : 'collapsed'}`}
										type="button"
										onClick={() => toggleAccordion()}
										aria-expanded={isOpenAccordion}
									>
										<div className="d-flex align-items-center justify-content-between">
											<p className="medium-14">
												Techpack Checklist : {designDetails?.checklistCheckedCount}/
												{designDetails?.checklistCount}
											</p>
											<div className="ml-2">
												<span
													className={`status ${TECHPACK_STATUS_TAG_COLORS[designDetails?.checklistStatus || 'PENDING']}`}
												>
													{designDetails?.checklistStatus || 'PENDING'}
												</span>
											</div>
										</div>
										<div className="arrow-down">
											<CommonImage
												src="/icons/down-arrow-gray-dark-02.svg"
												alt="arrow"
											/>
										</div>
									</button>
								</h2>
							</div>
							<div
								id="collapseCheck"
								className={`collapse ${isOpenAccordion ? 'show' : ''}`}
								aria-labelledby="headingChecklist"
								data-parent="#accordionExample"
							>
								<div className="card-body m-0 px-0 py-4 border-0">
									<div className="check-list-input-container">
										<div className="check-list-input-item d-grid grid-columns-2">
											<div className="left-item-content">
												<div className="row">
													<div className="col-12 p-0 mb-3">
														<div className="single-check-field d-flex align-items-center gap-2">
															<p className="title-and-checkbox with-out-checkbox">
																<span className="regular-14 weight-500 gray_dark_02 pl-0">
																	Special comments
																</span>
															</p>
															<div className="form-group mb-0">
																<textarea
																	placeholder="Enter styling & others"
																	rows={3}
																	value={stylingAndOther}
																	onChange={(e) =>
																		handleChange(
																			'stylingAndOther',
																			e.target.value
																		)
																	}
																	disabled={!isDesignEdit}
																/>
															</div>
														</div>
														<div className="single-check-field d-flex align-items-center gap-2">
															<div className="title-and-checkbox">
																<CheckboxItem
																	id="styleBlock"
																	label={`Style Block${designDetails?.isNitexProduct ? '*' : ''}`}
																	checked={
																		designDetails?.blockDetailsResponse
																			?.hasMeasurementChart ||
																		designDetails?.measurementChartInfoType ||
																		action?.value ||
																		designDetails
																			?.measurementChartInfoType
																			?.constant
																	}
																/>
															</div>
															<div className="w-100 position-relative">
																<div className="form-group mb-0">
																	<div className="style-block-dropdown d-flex align-items-center justify-content-between">
																		{!action?.id && (
																			<div className="action-result d-flex align-items-center">
																				{!action?.id ? (
																					<CustomTooltip
																						text={getBlockDetails()}
																					>
																						<p className="regular-14 weight-500 gray_dark_01 mb-0">
																							{!action?.value
																								? (
																										getBlockDetails() ||
																										''
																									)?.slice(
																										0,
																										35
																									)
																								: ''}
																						</p>
																					</CustomTooltip>
																				) : (
																					''
																				)}
																				{!action?.value &&
																					designDetails
																						?.measurementChartInfoType
																						?.constant &&
																					designDetails
																						?.measurementChartInfoType
																						?.constant !==
																						'BLOCK' && (
																						<CommonImage
																							className="cursor-pointer mr-1"
																							src="/icons/close.svg"
																							width={16}
																							height={16}
																							onClick={() => {
																								return action?.value
																									? handleChange(
																											'action',
																											null
																										)
																									: handleRemoveStyleBlock();
																							}}
																						/>
																					)}
																			</div>
																		)}
																		<span className="action-dropdown-box d-flex align-items-center">
																			<div className="form-group">
																				<CustomDropdown
																					title="Action"
																					items={CHECK_LIST_ACTIONS}
																					selectedItem={
																						action?.value
																					}
																					onItemClick={(value) => {
																						handleChange(
																							'action',
																							value
																						);
																					}}
																					isDisabled={!isDesignEdit}
																					position="right"
																					onClose={() => {
																						return action?.value
																							? handleChange(
																									'action',
																									null
																								)
																							: handleRemoveStyleBlock();
																					}}
																				/>
																			</div>
																			{action?.value && (
																				<CommonImage
																					className="cursor-pointer mr-1"
																					src="/icons/close.svg"
																					width={16}
																					height={16}
																					onClick={() => {
																						return action?.value
																							? handleChange(
																									'action',
																									null
																								)
																							: handleRemoveStyleBlock();
																					}}
																				/>
																			)}
																		</span>
																	</div>
																</div>
																<div className="form-group">
																	<CustomDropdown
																		items={fittingTypes?.map((item) => ({
																			...item,
																			value: item?.text
																		}))}
																		title="Fitting type"
																		onItemClick={(value) => {
																			handleChange(
																				'fittingType',
																				value
																			);
																		}}
																		selectedItem={fittingType?.value}
																		isDisabled={!isDesignEdit}
																	/>
																</div>
															</div>
														</div>
													</div>

													<div className="col-12 p-0 mb-3">
														<div className="single-check-field d-flex align-items-center gap-2">
															<div className="title-and-checkbox">
																<CheckboxItem
																	id="stitching"
																	label="Stitching"
																	checked={!!stitching}
																/>
															</div>
															<div className="form-group mb-0">
																<InputField
																	type="text"
																	value={stitching}
																	onChange={(e) =>
																		handleChange(
																			'stitching',
																			e.target.value
																		)
																	}
																	disabled={!isDesignEdit}
																/>
															</div>
														</div>
														<div className="single-check-field d-flex align-items-center gap-2">
															<div className="title-and-checkbox">
																<CheckboxItem
																	id="bodyFabric"
																	label="Body Fabric*"
																	checked={!!selectedBodyFabric?.name}
																/>
															</div>
															<div
																className="form-group mb-0"
																onClick={() =>
																	(isFashionDesigner() ||
																		checkAnyUserType([
																			'GARMENTS_TECH'
																		])) &&
																	toastError(
																		'You can’t add fabric from here, you need to book the fabric.'
																	)
																}
															>
																<CustomDropdown
																	title="Select"
																	items={bodyFabricList}
																	onItemSearch={(value) =>
																		onGetBodyFabrics(value)
																	}
																	onItemClick={(item) => {
																		onUpdateBodyFabric(
																			item,
																			'selectedBodyFabric'
																		);
																	}}
																	selectedItem={selectedBodyFabric?.name}
																	type="selectedBodyFabric"
																	toolTipLength={60}
																	onAddNewButtonClick={(value) => {
																		onParseInputName(
																			'MAIN_FABRIC',
																			value
																		);
																	}}
																	addNewButtonName="new fabric"
																	alwayShowAddNewButton={
																		!isFashionDesigner() ||
																		!checkAnyUserType(['GARMENTS_TECH'])
																	}
																	isDisabled={
																		!isDesignEdit ||
																		isFashionDesigner() ||
																		checkAnyUserType(['GARMENTS_TECH'])
																	}
																	isShowAddNewButton={false}
																/>
															</div>
														</div>
													</div>

													<div className="col-12 p-0">
														<div
															className={`single-check-field d-flex align-items-center gap-2 ${!isDesignEdit ? 'disabled-field' : ''}`}
														>
															<div className="title-and-checkbox">
																{print[0]?.value === 'N/A' ? (
																	renderCrossIcon('Print*')
																) : print?.length <= 0 ? (
																	<CheckboxItem
																		id="print"
																		label="Print*"
																		checked={
																			!!print[0]?.id !== 'NA' &&
																			!!print?.length > 0
																		}
																	/>
																) : (
																	<CheckboxItem
																		id="print"
																		label="Print*"
																		checked={
																			!!print[0]?.id !== 'NA' &&
																			!!print?.length > 0
																		}
																	/>
																)}
															</div>
															<div className="form-group mb-0 no-shadow-react-select">
																<CreatableSelect
																	placeholder="Select Print"
																	onChange={(value) => {
																		const newValues =
																			handleNASelection(value);
																		handleChange('print', newValues);
																	}}
																	value={print}
																	options={transformDropdownOptions(
																		printList
																	)}
																	isDisabled={!isDesignEdit}
																	isValidNewOption={() => isAdmin()}
																	onCreateOption={(value) => {
																		handleCreateNewPrint(value);
																	}}
																	isMulti
																	styles={noBorderStyles}
																/>
															</div>
														</div>
														<div
															className={`single-check-field d-flex align-items-center gap-2 ${!isDesignEdit ? 'disabled-field' : ''}`}
														>
															<div className="title-and-checkbox">
																{wash[0]?.value === 'N/A' ? (
																	renderCrossIcon('Wash*')
																) : wash?.length <= 0 ? (
																	<CheckboxItem
																		id="wash"
																		label="Wash*"
																		checked={
																			!!wash[0]?.id !== 'NA' &&
																			!!wash?.length > 0
																		}
																	/>
																) : (
																	<CheckboxItem
																		id="wash"
																		label="Wash*"
																		checked={
																			!!wash[0]?.id !== 'NA' &&
																			!!wash?.length > 0
																		}
																	/>
																)}
															</div>
															<div className="form-group mb-0 no-shadow-react-select">
																<CreatableSelect
																	placeholder="Select Wash"
																	onChange={(value) => {
																		const newValues =
																			handleNASelection(value);
																		handleChange('wash', newValues);
																	}}
																	value={wash}
																	options={transformDropdownOptions(
																		washList
																	)}
																	isMulti
																	styles={noBorderStyles}
																	isValidNewOption={() => isAdmin()}
																	isDisabled={!isDesignEdit}
																	onCreateOption={(value) => {
																		handleCreateWash(value);
																	}}
																/>
															</div>
														</div>
														<div
															className={`single-check-field d-flex align-items-center gap-2 ${!isDesignEdit ? 'disabled-field' : ''}`}
														>
															<div className="title-and-checkbox">
																{embroidery[0]?.value === 'N/A' ? (
																	renderCrossIcon('Embroidery*')
																) : embroidery?.length <= 0 ? (
																	<CheckboxItem
																		id="embroidery"
																		label="Embroidery*"
																		checked={
																			!!embroidery[0]?.id !== 'NA' &&
																			!!embroidery?.length > 0
																		}
																	/>
																) : (
																	<CheckboxItem
																		id="embroidery"
																		label="Embroidery*"
																		checked={
																			!!embroidery[0]?.id !== 'NA' &&
																			!!embroidery?.length > 0
																		}
																	/>
																)}
															</div>
															<div className="form-group mb-0 no-shadow-react-select">
																<CreatableSelect
																	placeholder="Embroidery"
																	onChange={(value) => {
																		const newValues =
																			handleNASelection(value);
																		handleChange('embroidery', newValues);
																	}}
																	value={embroidery}
																	options={transformDropdownOptions(
																		embroideryList
																	)}
																	isDisabled={!isDesignEdit}
																	onCreateOption={(name) => {
																		handleCreateEmbroidery(name);
																	}}
																	isValidNewOption={() => isAdmin()}
																	isMulti
																	styles={noBorderStyles}
																/>
															</div>
														</div>
													</div>
												</div>
											</div>
											<div className="right-item">
												<div className="col-12 p-0 mb-3">
													{!isFashionDesigner() && (
														<div className="single-check-field d-flex align-items-center gap-2">
															<div className="title-and-checkbox">
																{fabricDyeColor?.length > 0 ? (
																	<CheckboxItem
																		id="fabricDyeColor"
																		label="Fabric dye?"
																		checked={fabricDyeColor?.length > 0}
																		isShowNA={
																			fabricDyeColor?.length === 0
																		}
																	/>
																) : (
																	renderCrossIcon('Fabric dye?')
																)}
															</div>

															<div className="form-group mb-0">
																<SelectColor
																	selectedColorList={fabricDyeColor || []}
																	setSelectedColorList={(value) => {
																		handleChange('fabricDyeColor', value);
																	}}
																	setPantoneColorIds={() => {}}
																	placeholder="N/A"
																	isShowLabel={false}
																	isMultiple={false}
																	isDisabled={!isDesignEdit}
																/>
															</div>
														</div>
													)}
												</div>
												<div className="col-12 p-0 mb-3">
													{!isFashionDesigner() && (
														<div className="single-check-field d-flex align-items-center gap-2">
															<div className="title-and-checkbox">
																{trimsDyeColor?.length > 0 ? (
																	<CheckboxItem
																		id="trimsDyeColor"
																		label="Trims dye?"
																		checked={trimsDyeColor?.length > 0}
																		isShowNA={trimsDyeColor?.length === 0}
																	/>
																) : (
																	renderCrossIcon('Trims dye?')
																)}
															</div>

															<div className="form-group mb-0">
																<SelectColor
																	selectedColorList={trimsDyeColor || []}
																	setSelectedColorList={(value) =>
																		handleChange('trimsDyeColor', value)
																	}
																	setPantoneColorIds={() => {}}
																	placeholder="N/A"
																	isShowLabel={false}
																	isMultiple={false}
																	isDisabled={!isDesignEdit}
																/>
															</div>
														</div>
													)}
												</div>

												<div className="col-12 p-0 mb-3">
													<div className="single-check-field d-flex align-items-start gap-2">
														<div className="title-and-checkbox">
															<div className="d-flex align-items-center gap-2">
																<p className="regular-14 weight-500 gray_dark_02">
																	Additional fitting info
																</p>
															</div>
														</div>
														<div className="row">
															<div className="col-6 pr-2 pl-0 form-group mb-0">
																<CustomDropdown
																	title="Body Width"
																	items={formatFittingInfoEnum(
																		fittingInfoBodyWidth
																	)}
																	selectedItem={bodyWidth?.value}
																	onItemClick={(value) =>
																		handleChange('bodyWidth', value)
																	}
																	isDisabled={!isDesignEdit}
																/>
															</div>
															<div className="col-6 px-0 form-group mb-0">
																<CustomDropdown
																	title="Body Length"
																	items={formatFittingInfoEnum(
																		fittingInfoBodyLength
																	)}
																	selectedItem={bodyHeight?.value}
																	onItemClick={(value) =>
																		handleChange('bodyHeight', value)
																	}
																	isDisabled={!isDesignEdit}
																/>
															</div>
															<div className="col-12 px-0 form-group mt-2">
																<CustomDropdown
																	title="Sleeves"
																	items={formatFittingInfoEnum(
																		fittingInfoSleeves
																	)}
																	selectedItem={sleeves?.value}
																	onItemClick={(value) =>
																		handleChange('sleeves', value)
																	}
																	isDisabled={!isDesignEdit}
																/>
															</div>
														</div>
													</div>
												</div>

												{/* {designDetails?.mainFabricResponses?.length > 1 && (
                          <div className="col-12 p-0 mb-3">
                            <p className="regular-14 weight-500 gray_dark_02 mb-3">
                              Selected other fabric:
                            </p>
                            {designDetails?.mainFabricResponses?.length > 1 && (
                              <div className="d-flex align-items-center gap-2">
                                {designDetails?.mainFabricResponses
                                  .slice(1)
                                  ?.map((placement) => (
                                    <CheckboxItem
                                      key={placement?.id}
                                      id={placement?.id}
                                      label={
                                        placement
                                          ?.placementResponseList[0]
                                          ?.name
                                      }
                                      checked={
                                        !!placement
                                          ?.fabricDetailsTableResponse
                                          ?.fabricType
                                      }
                                    />
                                  ))}
                                {designDetails?.extraFabricResponses[0]?.placementResponseList?.map(
                                  (placement) => (
                                    <CheckboxItem
                                      key={placement?.key}
                                      id={placement?.name}
                                      label={placement?.name}
                                      checked={!!placement?.name}
                                    />
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        )} */}

												<div className="col-12 p-0 mb-3">
													{designDetails?.mainFabricResponses?.length > 1 && (
														<p className="regular-14 weight-500 gray_dark_02 mb-3">
															Selected other fabric:
														</p>
													)}
													{designDetails?.mainFabricResponses?.length > 1 && (
														<div className="d-flex align-items-center gap-2">
															{designDetails?.mainFabricResponses
																.slice(1)
																?.map((placement) => (
																	<CheckboxItem
																		key={placement?.id}
																		id={placement?.id}
																		label={
																			placement
																				?.placementResponseList[0]
																				?.name
																		}
																		checked={
																			!!placement
																				?.fabricDetailsTableResponse
																				?.fabricType
																		}
																	/>
																))}
														</div>
													)}

													<div className="col-12 p-0 mb-3">
														{designDetails?.extraFabricResponses?.length > 0 && (
															<p className="regular-14 weight-500 gray_dark_02 mb-3">
																Selected other fabric:
															</p>
														)}
														{designDetails?.extraFabricResponses?.length > 0 && (
															<div className="d-flex align-items-center gap-2">
																{designDetails?.extraFabricResponses?.map(
																	(placement) => (
																		<React.Fragment key={placement?.id}>
																			{
																				<CheckboxItem
																					id={placement?.id}
																					label={
																						placement
																							?.placementResponseList[0]
																							?.name
																					}
																					checked={
																						!!placement
																							?.fabricDetailsTableResponse
																							?.fabricType
																					}
																				/>
																			}
																			{placement?.placementResponseList
																				?.length > 1 && (
																				<span
																					style={{
																						marginLeft: '-6px'
																					}}
																				>
																					,
																				</span>
																			)}
																			{placement?.placementResponseList
																				?.slice(1)
																				?.map((item) => (
																					<span
																						className="regular-13 weight-500 gray_dark_02"
																						key={item?.name}
																					>
																						{item?.name}
																					</span>
																				))}
																		</React.Fragment>
																	)
																)}
															</div>
														)}
													</div>
												</div>

												{designDetails?.trimsResponses?.length > 0 && (
													<div className="col-12 p-0 mb-3">
														<p className="regular-14 weight-500 gray_dark_02 mb-3">
															Selected Trims:
														</p>
														<div className="d-flex flex-wrap align-items-center gap-2">
															{designDetails?.trimsResponses?.map((trim) => (
																<CheckboxItem
																	key={trim?.id}
																	id={
																		trim?.trimsAndBrandingTableResponse
																			?.subCategoryType?.category
																	}
																	label={
																		trim?.trimsAndBrandingTableResponse
																			?.subCategoryType?.category
																	}
																	checked={
																		trim?.trimsAndBrandingTableResponse
																			?.subCategoryType?.category
																	}
																/>
															))}
														</div>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default TechpackChecklist;
